import React, { KeyboardEvent, SyntheticEvent, useMemo } from "react";
import { PolyIcon, CancelButton, ToolTip, SubmitButton } from "elements";
import { useModalDispatch, ModalForm } from "collections/Modal";
import { Input, LabeledSelect } from "collections/Form";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { useInviteMemberMutation, useRolesQuery } from "generated/graphql";
import { useQueryParamBoolean } from "polyrouter";
import { DISMISS, useNotification } from "collections/Notifications";
import * as yup from "yup";
import { InferType } from "yup";
import { FormikErrors, FormikHelpers, Form, FieldArray } from "formik";
import i18n from "utilities/i18n";
import { OperationContext } from "urql";
import { RelevantTenantInfo, tenantTypeToDefaultRoleMap } from "./Helpers";
import { InternalRoleNames, MemberRoleNames, RoleName, RoleNames } from "roles";
import "./InviteMemberModal.scss";

const isValidInternalEmail = (email: string): boolean =>
  /@poly.com\s*$/.test(email);

export const InviteMemberModalSchema = yup.object({
  email: yup
    .string()
    .default("")
    .email(i18n.t("INVALID_EMAIL"))
    .label(i18n.t("EMAIL_ADDRESS"))
    .test("Email is in list", i18n.t("IS_IN_LIST"), function(email) {
      const { emails } = this.parent;
      if (emails.length) {
        const regex = new RegExp(emails.join("|"), "i"); // this adds case insensitivity to the list of emails
        return !regex.test(email);
      } else {
        return true;
      }
    })
    .test(
      "Member is already on account",
      i18n.t("MEMBER_ALREADY_ON_ACCOUNT"),
      function(email) {
        const tenantMemberEmails = this.parent.tenantMemberEmails as string[];
        if (tenantMemberEmails.length) {
          const regex = new RegExp(tenantMemberEmails.join("|"), "i"); // this adds case insensitivity to the list of emails
          return !regex.test(email);
        } else {
          return true;
        }
      }
    )
    .test(
      "internal-email-required",
      i18n.t("INTERNAL_EMAIL_REQUIRED"),
      function(email) {
        return (
          !InternalRoleNames.includes(this.parent.role) ||
          isValidInternalEmail(email)
        );
      }
    ),
  emails: yup
    .array()
    .default([])
    .of(yup.string()),
  role: yup
    .string()
    .label(i18n.t("ROLE"))
    .matches(new RegExp(RoleNames.join("|"))),
  tenantMemberEmails: yup
    .array()
    .of(yup.string())
    .default([])
});

export type InviteFormProps = InferType<typeof InviteMemberModalSchema>;

type Email = string;
export type DeviceUser = "device-user";

export const InviteMemberModal: React.FC<{
  tenant: RelevantTenantInfo;
  tags?: string[];
  userEmail?: string;
  userEmails?: string[];
  selectedRole?: string;
  memberEmails?: string[];
  executor?: (opts?: Partial<OperationContext> | undefined) => void;
}> = ({
  tenant,
  tags = MemberRoleNames,
  userEmail,
  userEmails,
  selectedRole,
  memberEmails,
  executor
}) => {
  const { t } = useTranslation();
  const dispatchEvent = useModalDispatch();
  const [deviceUser] = useQueryParamBoolean<DeviceUser>("device-user");

  const [addNotification] = useNotification();
  const [, sendInvite] = useInviteMemberMutation();

  const { data } = useRolesQuery({ variables: { tags } });

  const roles: RoleName[] = ((data && data.roles) || []).map(
    roleProps => roleProps.name
  );

  const title = `${t("INVITATION_TO")} ${tenant.name || ""}`;

  const handleSubmit = async (
    values: InviteFormProps,
    formikHelpers: FormikHelpers<InviteFormProps>
  ) => {
    const emailsToSend: Array<Email> =
      values.emails.length !== 0 ? values.emails : [];

    if (values.email) {
      await sendEmails(
        [...emailsToSend, values.email],
        values.role,
        formikHelpers.setErrors
      );
    } else if (emailsToSend.length !== 0) {
      await sendEmails(emailsToSend, values.role, formikHelpers.setErrors);
    } else {
      formikHelpers.resetForm();
    }
  };

  const sendEmails = async (
    emails: Array<Email>,
    role: string,
    setErrors: (errors: FormikErrors<object & { email: string }>) => void
  ) => {
    const inviteRes = await sendInvite({
      emails: emails,
      tenant_id: tenant.id,
      role: role
    });

    const inviteUser = inviteRes.data?.inviteUser;
    if (inviteUser) {
      //check that all emails were successfully sent
      if (inviteUser?.invites?.every((invite: any) => invite.success)) {
        addNotification({
          type: "success",
          message: t("EMAIL_SENT", { count: emails.length }),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
        //refresh the pending invites list
        if (executor) {
          executor({
            requestPolicy: "network-only"
          });
        }
        dispatchEvent();
        return;
      }

      inviteUser?.invites?.forEach(invite => {
        if (invite.error) {
          addNotification({
            type: "error",
            id: "error-" + invite.email,
            message: t("INVITATION_ERROR_TO", {
              email: invite.email,
              message: invite.error
            }),
            dismiss: DISMISS.DEFAULT
          });
        }
      });
    }

    setErrors({ email: `${t("TRY_AGAIN")}` });
    return;
  };

  const handleEmailChange = (
    event: SyntheticEvent,
    isValid: boolean,
    setFieldValue: (
      field: keyof InviteFormProps,
      value: any,
      shouldValidate?: boolean
    ) => void,
    pushValue: (obj: any) => void,
    validateField: (field: string) => void
  ) => {
    validateField("email");
    if (
      ["Enter", "Tab", ",", ";", " "].includes((event as KeyboardEvent).key)
    ) {
      event.preventDefault();
      const inputValue = (event.currentTarget as HTMLTextAreaElement).value;
      if (inputValue === "") {
        return;
      } else if (isValid) {
        setFieldValue("email", "", false);
        pushValue(inputValue);
      }
    }
  };

  const handlePaste = (
    event: SyntheticEvent,
    setFieldValue: (
      field: keyof InviteFormProps,
      value: any,
      shouldValidate?: boolean
    ) => void,
    setErrors: (errors: FormikErrors<object & { email: string }>) => void,
    values: InviteFormProps
  ) => {
    event.preventDefault();
    const data = (event as ClipboardEventInit).clipboardData;
    if (data) {
      const paste = data.getData("text");
      setFieldValue("email", paste, false);
      const newEmails = paste.match(/[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/g);
      const emails = values.emails ?? [];

      // Filter out any email addresses from the paste event
      // that might already be on the current account
      let filteredEmails: string[] = [];
      if (memberEmails && memberEmails.length) {
        const regex = new RegExp(memberEmails.join("|"), "i");
        newEmails?.forEach(email => {
          if (!regex.test(email)) {
            filteredEmails.push(email);
          }
        });
      } else if (!memberEmails && newEmails) {
        filteredEmails.concat(newEmails);
      }

      if (filteredEmails.length > 0) {
        setFieldValue("emails", [...emails, ...filteredEmails]);
        setFieldValue("email", "");
        return;
      }
    }
    setErrors({ email: `${t("TRY_AGAIN")}` });
    return;
  };

  const initialValues: InviteFormProps = useMemo(() => {
    const emails: string[] = [];
    if (userEmail) {
      emails.push(userEmail);
    }
    if (userEmails) {
      emails.push(...userEmails);
    }
    return {
      email: InviteMemberModalSchema.fields.email.default(),
      emails: emails ?? [],
      role: selectedRole
        ? selectedRole
        : deviceUser
        ? "device_user"
        : tenant.id === "*"
        ? "poly-support"
        : tenantTypeToDefaultRoleMap[tenant.type],
      tenantMemberEmails: memberEmails ?? []
    };
  }, [deviceUser, memberEmails, selectedRole, tenant, userEmails, userEmail]);

  const addressTooltip = () => {
    return (
      <ToolTip
        size="lg"
        position="top"
        icon="help_circle"
        iconTitle="help"
        className="ml-14 mb-0 help"
      >
        {t("ENTER_ADDRESS_TOOLTIP_INFO")}
      </ToolTip>
    );
  };

  return (
    <ModalForm
      title={title}
      className="invite-member"
      name="InviteMembers"
      onSubmit={handleSubmit}
      fluid={true}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={InviteMemberModalSchema}
      validateOnChange
    >
      {({
        isValid,
        dirty,
        values,
        isSubmitting,
        setFieldValue,
        setErrors,
        validateField
      }) => {
        return (
          <Form>
            <div>
              {isSubmitting ? (
                <p>{t("SENDING")}</p>
              ) : (
                <>
                  {!deviceUser && (
                    <span>
                      <div>
                        {t("ENTER_ADDRESS_INSTRUCTIONS")}
                        {addressTooltip()}
                        {`${t("ENTER_ADDRESS_INSTRUCTIONS_ROLE")}
                               ${t("ENTER_ADDRESS_INSTRUCTIONS_PASTE")}`}
                      </div>
                      <p className="mt-5">
                        {t("ENTER_ADDRESS_INSTRUCTIONS_2")}
                      </p>
                    </span>
                  )}
                  {deviceUser && (
                    <div className="mb-5">
                      {`${t("ENTER_ADDRESS_INSTRUCTIONS")}.`}
                      {addressTooltip()}
                      {t("ENTER_ADDRESS_INSTRUCTIONS_PASTE")}
                    </div>
                  )}
                  <FieldArray
                    name="emails"
                    render={arrayHelpers => (
                      <>
                        <div className="form-content">
                          <Input
                            data-testid="email-input"
                            inputClassName="material on-black form-control"
                            id="email"
                            name="email"
                            label={t("EMAIL_ADDRESS")}
                            onKeyDown={event =>
                              handleEmailChange(
                                event,
                                isValid,
                                setFieldValue,
                                arrayHelpers.push,
                                validateField
                              )
                            }
                            onPaste={event =>
                              handlePaste(
                                event,
                                setFieldValue,
                                setErrors,
                                values
                              )
                            }
                          />
                        </div>
                        <div className="scroll-content">
                          {values.emails.map((email, i) => (
                            <span
                              className="email-chip mt-5"
                              data-testid="email-chip"
                              key={email}
                            >
                              <span className="grid">
                                <span className="col-10 p-5 v-center">
                                  {email}
                                </span>
                                <span
                                  onClick={() => arrayHelpers.remove(i)}
                                  className="col-2 text-right v-center"
                                >
                                  <PolyIcon
                                    icon="clear_x"
                                    className="clear-chip"
                                  />
                                </span>
                              </span>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {!deviceUser && (
                    <div className="floating-label-filter grid mt-1">
                      <div className="col-12 pl-0" data-testid="dropdown">
                        <LabeledSelect
                          name="role"
                          options={roles.map(role => ({
                            text: t(role as LocalKeys),
                            value: role
                          }))}
                          label={t("ROLE")}
                        />
                      </div>
                    </div>
                  )}
                  <div className="btn-container">
                    <CancelButton onClick={dispatchEvent} />
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      testId="send"
                      disabled={
                        (!isValid || !dirty) && values.emails.length === 0
                      }
                      labelKey="ADD"
                      submittingLabelKey="ADDING"
                    />
                  </div>
                </>
              )}
            </div>
          </Form>
        );
      }}
    </ModalForm>
  );
};

export default InviteMemberModal;
